import React, { useState, useRef } from "react";
import { Modal, Button } from "react-bootstrap";
import { format } from "date-fns";
import { generatePdfFromElement, copyElementToClipboardAsImage } from "../../utils/pdfUtils";

function OrderDetailsModal({ show, onHide, order }) {
  const [activeTab, setActiveTab] = useState("customer");
  const [downloadingPdf, setDownloadingPdf] = useState(false);
  const printRef = useRef(null);

  const handleHide = () => {
    setActiveTab("customer");
    onHide();
  };

  const generateInvoiceMessage = () => {
    if (!order) return "";

    let storeName = "";
    let storePhone = "";
    let storeId = "";
    let storeAddress = "";

    try {
      const stored = localStorage.getItem("user");
      if (stored) {
        const parsed = JSON.parse(stored);
        storeName = parsed.store_name || parsed.user_name || "";
        storePhone = parsed.mobile_no || parsed.phone || "";
        storeId = parsed.store_id || "";
        const addrParts = [parsed.address, parsed.area, parsed.city, parsed.state, parsed.country, parsed.pincode].filter(Boolean);
        if (addrParts.length > 0) {
          storeAddress = addrParts.join(", ");
        }
      }
    } catch (e) {
      console.error(e);
    }

    const customerName = order.customer?.name || "Grahak";
    const invoiceNo = String(order.invoice?.number || "").replace(/^ORD-/, "");
    const dateStr = order.invoice?.date || new Date().toLocaleDateString("en-IN");

    let text = `*નમસ્તે ${customerName},*\n\n`;
    text += `આપની ખરીદી બદલ હૃદયપૂર્વક આભાર.\n`;
    text += `Order નંબર #${invoiceNo} માટેનો ઇન્વોઇસ PDF સ્વરૂપે અહીં જોડેલ છે — કૃપા કરીને તપાસ કરો.\n\n`;
    if (storePhone) {
      text += `જો કોઈ પ્રશ્ન કે સહાયની જરૂર હોય, તો નિઃસંકોચ અમારો સંપર્ક કરો (+91 ${storePhone}).\n\n`;
    }
    text += `*BILIPATRA*\n`;
    text += `--------------------------------\n`;
    text += `${storeName || "Retail Counter"}\n`;
    if (storeAddress) {
      text += `*સરનામું (Address):* ${storeAddress}\n`;
    }
    if (storePhone) {
      text += `*સ્ટોર સંપર્ક (Store Phone):* +91 ${storePhone}\n`;
    }
    text += `*બિલ નંબર (Invoice No):* #${invoiceNo}\n`;
    text += `*તારીખ (Date):* ${dateStr}\n`;
    if (order.customer?.contact) {
      text += `*ગ્રાહક મોબાઇલ (Mobile):* ${order.customer.contact}\n`;
    }
    text += `--------------------------------\n`;
    text += `*ઓર્ડર વિગત (Items Ordered):*\n`;

    order.product_list?.forEach((item, index) => {
      const price = parseFloat(item.price || 0);
      const qty = parseFloat(item.qty || 0);
      const disc = parseFloat(item.discount || 0);
      const finalAmt = parseFloat(item.final_amt || item.net_amount || (price * qty - disc));
      const weight = item.product_weight ? ` (${item.product_weight})` : "";

      text += `${index + 1}. *${item.product_name}${weight}*\n`;
      text += `   Qty: ${qty} × ₹${price.toFixed(2)}`;
      if (disc > 0) {
        text += ` (-₹${disc.toFixed(2)}) = *₹${finalAmt.toFixed(2)}*\n`;
      } else {
        text += ` = *₹${finalAmt.toFixed(2)}*\n`;
      }
    });

    text += `--------------------------------\n`;
    const discountVal = parseFloat(order.summary?.discount || 0);
    if (discountVal > 0) {
      text += `કુલ રકમ (Sub Total): ₹${order.summary?.sub_total}\n`;
      text += `ડિસ્કાઉન્ટ (Discount): -₹${discountVal.toFixed(2)}\n`;
    }
    text += `*કુલ ચૂકવવાપાત્ર રકમ (Grand Total): ₹${order.summary?.total}*\n`;
    const paymentStatus = order.invoice?.order_type?.toLowerCase() === "credit" && balanceDue > 0
      ? `બાકી (Credit / Udhar - Due: ₹${balanceDue.toFixed(2)})`
      : `ચૂકવેલ (PAID - ${String(order.invoice?.order_type || "CASH").toUpperCase()})`;
    text += `*ચૂકવણી (Payment):* ${paymentStatus}\n`;
    text += `--------------------------------\n`;
    text += `_બિલીપત્રા પસંદ કરવા બદલ આપનો ખૂબ ખૂબ આભાર!_`;

    return text;
  };

  const [defaultWaMethod, setDefaultWaMethod] = useState(
    localStorage.getItem("preferredWaMethod") || "api"
  );

  const handleShareWhatsApp = async (method = defaultWaMethod) => {
    if (!order) return;

    // Save as new default
    localStorage.setItem("preferredWaMethod", method);
    setDefaultWaMethod(method);

    const text = generateInvoiceMessage();
    const rawPhone = String(order.customer?.contact || "").trim().replace(/\D/g, "");
    
    let targetPhone = "";
    if (rawPhone.length === 10) {
      targetPhone = `91${rawPhone}`;
    } else if (rawPhone.length > 10) {
      targetPhone = rawPhone.startsWith("91") ? rawPhone : `91${rawPhone.slice(-10)}`;
    }

    const encodedText = encodeURIComponent(text);
    let waUrl = "";

    if (method === 'share') {
      try {
        if (navigator.canShare) {
          const htmlContent = generatePrintHtml();
          const tempDiv = document.createElement("div");
          tempDiv.innerHTML = htmlContent;
          tempDiv.style.position = "absolute";
          tempDiv.style.top = "-9999px";
          tempDiv.style.left = "-9999px";
          tempDiv.style.width = "750px";
          document.body.appendChild(tempDiv);

          const element = tempDiv.firstElementChild;
          const file = await generatePdfFromElement(element, `Bill_ORD-${order.order_id || "receipt"}.pdf`, "file");
          document.body.removeChild(tempDiv);

          if (navigator.canShare({ files: [file] })) {
            await navigator.share({
              files: [file],
              title: `Bill_ORD-${order.order_id || "receipt"}`,
              text: text
            });
            return;
          }
        }
      } catch (err) {
        console.error("Native share failed:", err);
      }
      alert("Your browser/OS does not support native PDF sharing.");
      return;
    } else if (method === 'app') {
      waUrl = targetPhone ? `whatsapp://send?phone=${targetPhone}&text=${encodedText}` : `whatsapp://send?text=${encodedText}`;
      window.location.href = waUrl;
      return;
    } else if (method === 'web') {
      waUrl = targetPhone ? `https://web.whatsapp.com/send?phone=${targetPhone}&text=${encodedText}` : `https://web.whatsapp.com/send?text=${encodedText}`;
      window.open(waUrl, "whatsapp_web_window");
      return;
    } else {
      waUrl = targetPhone
        ? `https://wa.me/${targetPhone}?text=${encodedText}`
        : `https://wa.me/?text=${encodedText}`;
      window.open(waUrl, "whatsapp_api_window");
      return;
    }
  };

  const handleCopyMessage = async () => {
    if (!order) return;
    const text = generateInvoiceMessage();
    try {
      await navigator.clipboard.writeText(text);
      alert("Invoice message copied to clipboard!");
    } catch (err) {
      console.error("Failed to copy text: ", err);
      alert("Failed to copy message. Please try manually.");
    }
  };

  const generatePrintHtml = () => {
    if (!order) return "";
    let storeName = "";
    let storePhone = "";
    let storeId = "";
    let storeAddress = "";

    try {
      const stored = localStorage.getItem("user");
      if (stored) {
        const parsed = JSON.parse(stored);
        storeName = parsed.store_name || parsed.user_name || "";
        storePhone = parsed.mobile_no || parsed.phone || "";
        storeId = parsed.store_id || "";
        const addrParts = [parsed.address, parsed.area, parsed.city, parsed.state, parsed.country, parsed.pincode].filter(Boolean);
        if (addrParts.length > 0) {
          storeAddress = addrParts.join(", ");
        }
      }
    } catch (e) {
      console.error(e);
    }
    const invoiceNo = String(order.invoice?.number || "").replace(/^ORD-/, "");
    const dateStr = order.invoice?.date || new Date().toLocaleDateString("en-IN");
    
    const itemsHtml = (order.product_list || []).map((item, idx) => `
      <tr>
        <td style="padding: 6px 8px; border-bottom: 1px solid #e2e8f0; text-align: center;">${idx + 1}</td>
        <td style="padding: 6px 8px; border-bottom: 1px solid #e2e8f0;"><b>${item.product_name}</b> <span style="font-size: 10px; color: #64748b;">${item.product_weight || ''}</span></td>
        <td style="padding: 6px 8px; border-bottom: 1px solid #e2e8f0; text-align: center;">${item.qty} ${item.unit || ''}</td>
        <td style="padding: 6px 8px; border-bottom: 1px solid #e2e8f0; text-align: right;">₹${item.price}</td>
        <td style="padding: 6px 8px; border-bottom: 1px solid #e2e8f0; text-align: right; color: #dc2626;">${parseFloat(item.discount || 0) > 0 ? '-₹' + item.discount : '-'}</td>
        <td style="padding: 6px 8px; border-bottom: 1px solid #e2e8f0; text-align: right; font-weight: bold;">₹${item.final_amt || item.net_amount}</td>
      </tr>
    `).join('');

    return `
      <div style="max-width: 700px; margin: 0 auto; border: 1px solid #e2e8f0; padding: 24px; border-radius: 8px; background: #ffffff;">
        <div style="display: flex; justify-content: space-between; align-items: flex-start; border-bottom: 2px solid #198754; padding-bottom: 12px; margin-bottom: 16px;">
          <div style="max-width: 52%;">
            <h2 style="margin: 0; color: #198754; font-weight: 800;">BILIPATRA</h2>
            <div style="font-weight: 700; font-size: 12px; color: #1e293b; margin-top: 1px;">Vraj Prachin Ayurved Chikitsa Kendra</div>
            ${storeAddress ? `<div style="font-size: 10px; color: #64748b; margin-top: 2px; line-height: 1.3;">${storeAddress}</div>` : ''}
            ${storePhone ? `<div style="font-size: 10px; color: #334155; margin-top: 1px;">Store: <b>+91 ${storePhone}</b></div>` : ''}
          </div>
          <div style="text-align: right; max-width: 45%;">
            <div style="display: inline-block; background: #e8f5e9; color: #1b5e20; font-weight: bold; font-size: 10px; padding: 2px 8px; border-radius: 4px; margin-bottom: 3px;">RETAIL BILL / CASH MEMO</div>
            ${storeName ? `<div style="font-weight: bold; font-size: 12px; color: #0f172a;">${storeName}${storeId ? ` (Branch #${storeId})` : ''}</div>` : ''}
          </div>
        </div>
        
        <div style="display: grid; grid-template-columns: 1fr 1fr; gap: 12px; background: #f8fafc; padding: 10px 14px; border-radius: 6px; margin-bottom: 16px; font-size: 12px;">
          <div>
            <div style="font-size: 10px; font-weight: bold; color: #64748b; text-transform: uppercase;">Customer Details</div>
            <div style="font-weight: bold; font-size: 13px;">${order.customer?.name || 'Walk-in Customer'}</div>
            <div>Phone: +91 ${order.customer?.contact || 'N/A'}</div>
            <div style="font-size: 11px; color: #64748b;">Address: ${order.customer?.address || 'Walk-in'}</div>
          </div>
          <div style="text-align: right;">
            <div style="font-size: 10px; font-weight: bold; color: #64748b; text-transform: uppercase;">Invoice Details</div>
            <div style="font-weight: bold; font-size: 13px;">Invoice No: <span style="color: #198754;">#${invoiceNo}</span></div>
            <div>Date: ${dateStr}</div>
            <div>Payment: <b>${String(order.invoice?.order_type || 'CASH').toUpperCase()}</b></div>
          </div>
        </div>

        <table style="width: 100%; border-collapse: collapse; font-size: 12px; margin-bottom: 16px;">
          <thead>
            <tr style="background: #198754; color: white;">
              <th style="padding: 6px 8px; text-align: center; width: 30px;">#</th>
              <th style="padding: 6px 8px; text-align: left;">Item Description</th>
              <th style="padding: 6px 8px; text-align: center; width: 50px;">Qty</th>
              <th style="padding: 6px 8px; text-align: right; width: 80px;">Rate (₹)</th>
              <th style="padding: 6px 8px; text-align: right; width: 75px;">Discount</th>
              <th style="padding: 6px 8px; text-align: right; width: 85px;">Amount (₹)</th>
            </tr>
          </thead>
          <tbody>
            ${itemsHtml}
          </tbody>
        </table>

        <div style="display: flex; justify-content: flex-end; margin-bottom: 16px;">
          <div style="width: 250px; font-size: 12px;">
            <div style="display: flex; justify-content: space-between; margin-bottom: 4px;">
              <span>Sub Total:</span>
              <span>₹${order.summary?.sub_total}</span>
            </div>
            ${parseFloat(order.summary?.discount || 0) > 0 ? `
              <div style="display: flex; justify-content: space-between; margin-bottom: 4px; color: #dc2626;">
                <span>Total Discount:</span>
                <span>-₹${order.summary?.discount}</span>
              </div>
            ` : ''}
            <div style="display: flex; justify-content: space-between; font-weight: bold; font-size: 14px; border-top: 1px solid #e2e8f0; padding-top: 6px; color: #198754;">
              <span>Grand Total:</span>
              <span>₹${order.summary?.total}</span>
            </div>
          </div>
        </div>

        <div style="border-top: 1px solid #e2e8f0; padding-top: 12px; font-size: 10px; color: #64748b; display: flex; justify-content: space-between;">
          <div>
            <div>* Computer generated invoice. No signature required.</div>
            <div style="color: #198754; font-weight: bold; margin-top: 2px;">Thank you for choosing BILIPATRA!</div>
          </div>
          <div style="text-align: center; border-top: 1px solid #94a3b8; width: 120px; padding-top: 2px; font-weight: bold;">
            Authorized Signatory
          </div>
        </div>
      </div>
    `;
  };

  const handlePrint = () => {
    if (!order) return;
    const invoiceNo = String(order.invoice?.number || "").replace(/^ORD-/, "");
    const printWindow = window.open("", "_blank", "width=850,height=900");
    if (!printWindow) {
      window.print();
      return;
    }

    printWindow.document.write(`
      <!DOCTYPE html>
      <html>
        <head>
          <title>Invoice #${invoiceNo}</title>
          <style>
            body { margin: 0; padding: 20px; font-family: 'Segoe UI', Arial, sans-serif; color: #1e293b; background: #f8fafc; }
            @media print { body { margin: 0; padding: 10px; background: white; } @page { size: auto; margin: 8mm; } }
          </style>
        </head>
        <body>
          ${generatePrintHtml()}
        </body>
      </html>
    `);
    printWindow.document.close();
    printWindow.focus();
    setTimeout(() => {
      printWindow.print();
      printWindow.close();
    }, 400);
  };

  const [defaultDocMethod, setDefaultDocMethod] = useState(
    localStorage.getItem("preferredDocMethod") || "print"
  );

  const handleDocumentAction = (method = defaultDocMethod) => {
    localStorage.setItem("preferredDocMethod", method);
    setDefaultDocMethod(method);
    if (method === 'pdf') {
      handleDownloadPDF();
    } else if (method === 'copy_img') {
      handleCopyImage();
    } else {
      handlePrint();
    }
  };

  const handleCopyImage = async () => {
    if (!order) return;
    try {
      setDownloadingPdf(true); // Re-using state for loading spinner
      const htmlContent = generatePrintHtml();
      const tempDiv = document.createElement("div");
      tempDiv.innerHTML = htmlContent;
      tempDiv.style.position = "absolute";
      tempDiv.style.top = "-9999px";
      tempDiv.style.left = "-9999px";
      tempDiv.style.width = "750px";
      document.body.appendChild(tempDiv);
      const element = tempDiv.firstElementChild;

      const success = await copyElementToClipboardAsImage(element);
      document.body.removeChild(tempDiv);
      
      if (success) {
        alert("Receipt copied to clipboard as an image! You can now paste it.");
      } else {
        alert("Failed to copy receipt to clipboard.");
      }
    } catch (err) {
      console.error("Clipboard copy failed:", err);
    } finally {
      setDownloadingPdf(false);
    }
  };

  const handleDownloadPDF = async () => {
    if (!order) return;
    try {
      setDownloadingPdf(true);
      const htmlContent = generatePrintHtml();
      const tempDiv = document.createElement("div");
      tempDiv.innerHTML = htmlContent;
      tempDiv.style.position = "absolute";
      tempDiv.style.top = "-9999px";
      tempDiv.style.left = "-9999px";
      tempDiv.style.width = "750px";
      document.body.appendChild(tempDiv);
      const element = tempDiv.firstElementChild;

      await generatePdfFromElement(element, `Bill_ORD-${order?.order_id || "receipt"}.pdf`, "download");
      document.body.removeChild(tempDiv);
    } catch (err) {
      console.error("PDF generation failed:", err);
      alert("Failed to generate PDF. You can use 'Print' to save as PDF.");
    } finally {
      setDownloadingPdf(false);
    }
  };

  if (!order) return null;

  // 🟢 Extracting Credit Data safely
  const balanceDue = parseFloat(order.summary?.balance_due || 0);
  const isCredit =
    order.invoice?.order_type?.toLowerCase() === "credit" && balanceDue > 0;

  // 🟢 Dynamically generate tabs based on available data
  const tabs = ["customer", "order"];
  if (order.payment_history && order.payment_history.length > 0) {
    tabs.push("payments");
  }

  return (
    <Modal show={show} onHide={handleHide} size="xl" centered>
      <Modal.Header closeButton className="bg-light">
        <Modal.Title className="fw-bold d-flex align-items-center gap-2">
          Order: {order.invoice?.number}
          {isCredit && (
            <span className="badge bg-warning text-dark fs-6 ms-2">
              CREDIT / DUE
            </span>
          )}
        </Modal.Title>
      </Modal.Header>
      <Modal.Body className="p-0">
        <ul
          className="nav nav-tabs nav-tabs-bordered px-3 pt-2 bg-light"
          role="tablist"
        >
          {tabs.map((tab) => (
            <li className="nav-item" key={tab} role="presentation">
              <button
                className={`nav-link fw-bold ${activeTab === tab ? "active" : ""}`}
                onClick={() => setActiveTab(tab)}
                type="button"
                role="tab"
              >
                {tab === "customer"
                  ? "Customer Details"
                  : tab === "order"
                  ? "Order Breakdown"
                  : "Payment History"}
              </button>
            </li>
          ))}
        </ul>

        <div className="tab-content p-4">
          {/* 🟢 CUSTOMER TAB */}
          {activeTab === "customer" && (
            <div className="tab-pane fade show active">
              <div className="row">
                <div className="col-md-6 mb-3">
                  <label className="text-muted small fw-bold">
                    Customer Name
                  </label>
                  <div className="fs-5">
                    {order.customer?.name || "Walk-in Customer"}
                  </div>
                </div>
                <div className="col-md-6 mb-3">
                  <label className="text-muted small fw-bold">
                    Mobile Number
                  </label>
                  <div className="fs-5">{order.customer?.contact || "N/A"}</div>
                </div>
                <div className="col-md-12 mb-3">
                  <label className="text-muted small fw-bold">
                    Billing Address
                  </label>
                  <div>{order.customer?.address || "N/A"}</div>
                </div>
              </div>
            </div>
          )}

          {/* 🟢 ORDER BREAKDOWN TAB */}
          {activeTab === "order" && (
            <div className="tab-pane fade show active">
              <div className="row mb-4 bg-light p-3 rounded">
                <div className="col-md-3">
                  <div className="text-muted small fw-bold">Invoice Date</div>
                  <div className="fw-semibold">{order.invoice?.date}</div>
                </div>
                <div className="col-md-3">
                  <div className="text-muted small fw-bold">Payment Method</div>
                  <div className="fw-semibold text-uppercase">
                    {order.invoice?.order_type}
                  </div>
                </div>
                <div className="col-md-3">
                  <div className="text-muted small fw-bold">
                    Global Cart Discount
                  </div>
                  <div className="fw-semibold">
                    {order.invoice?.discount_percent}%
                  </div>
                </div>
              </div>

              <h6 className="fw-bold mb-3 text-primary">
                <i className="bi bi-box-seam me-2"></i>Product Line Items
              </h6>
              <div className="table-responsive mb-4">
                <table className="table table-bordered table-hover table-sm align-middle text-center">
                  <thead className="table-light">
                    <tr>
                      <th>#</th>
                      <th className="text-start">Item</th>
                      <th>Unit Price</th>
                      <th>Qty</th>
                      <th>Gross</th>
                      <th>Discount</th>
                      <th>Net Amt</th>
                      <th>Tax (GST)</th>
                      <th className="bg-light">Final Total</th>
                    </tr>
                  </thead>
                  <tbody>
                    {order.product_list?.map((product, index) => {
                      const gross =
                        parseFloat(product.price) * parseFloat(product.qty);
                      const hasOverride =
                        parseFloat(product.item_discount_percent) > 0;
                      const hasGlobal =
                        parseFloat(order.invoice?.discount_percent) > 0 &&
                        !hasOverride;

                      return (
                        <tr key={index}>
                          <td>{index + 1}</td>
                          <td className="text-start fw-bold">
                            {product.product_name}
                            <div className="text-muted small fw-normal">
                              {product.product_weight}
                            </div>
                          </td>
                          <td>₹{product.price}</td>
                          <td>
                            {product.qty} {product.unit}
                          </td>
                          <td className="text-muted">₹{gross.toFixed(2)}</td>
                          <td>
                            <div className="text-danger fw-semibold">
                              -₹{product.discount}
                            </div>
                            {hasOverride && (
                              <span
                                className="badge bg-warning text-dark mt-1"
                                style={{
                                  fontSize: "10px",
                                  letterSpacing: "0.5px",
                                }}
                              >
                                {product.item_discount_percent}% OVERRIDE
                              </span>
                            )}
                            {hasGlobal && (
                              <span
                                className="badge bg-light text-secondary border mt-1"
                                style={{ fontSize: "10px" }}
                              >
                                {order.invoice?.discount_percent}% Global
                              </span>
                            )}
                          </td>
                          <td>₹{product.net_amount}</td>
                          <td className="text-muted">₹{product.gst}</td>
                          <td className="fw-bold text-success bg-light">
                            ₹{product.final_amt}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>

              {/* 🟢 RECEIPT PAYMENT SUMMARY */}
              <div className="row justify-content-end">
                <div className="col-md-5">
                  <div className="card shadow-sm border-0 bg-light">
                    <div className="card-body p-4">
                      <h6 className="fw-bold mb-3 border-bottom pb-2">
                        Payment Summary
                      </h6>

                      <div className="d-flex justify-content-between mb-2">
                        <span className="text-muted">
                          Sub Total (Incl. Taxes)
                        </span>
                        <span className="fw-semibold">
                          ₹{order.summary?.sub_total}
                        </span>
                      </div>

                      {parseFloat(order.summary?.discount) > 0 && (
                        <div className="d-flex justify-content-between mb-2">
                          <span className="text-muted">Total Discounts</span>
                          <span className="text-danger fw-semibold">
                            -₹{order.summary?.discount}
                          </span>
                        </div>
                      )}

                      <div className="d-flex justify-content-between mb-1 small text-muted">
                        <span>SGST Included</span>
                        <span>₹{order.summary?.sgst}</span>
                      </div>
                      <div className="d-flex justify-content-between mb-3 small text-muted">
                        <span>CGST Included</span>
                        <span>₹{order.summary?.cgst}</span>
                      </div>

                      <hr className="my-2 border-secondary" />

                      <div className="d-flex justify-content-between mb-3">
                        <span className="fw-bold fs-5 text-dark">
                          Grand Total
                        </span>
                        <span className="fw-bold fs-5 text-dark">
                          ₹{order.summary?.total}
                        </span>
                      </div>

                      <div className="d-flex justify-content-between align-items-center mb-2 p-2 bg-success bg-opacity-10 rounded">
                        <span className="fw-bold text-success">
                          Amount Paid
                        </span>
                        <span className="fw-bold text-success fs-6">
                          ₹{order.summary?.paid_amount ?? 0}
                        </span>
                      </div>

                      {balanceDue > 0 && (
                        <div className="d-flex justify-content-between align-items-center p-2 bg-danger bg-opacity-10 rounded">
                          <span className="fw-bold text-danger">
                            Balance Due
                          </span>
                          <span className="fw-bold text-danger fs-5">
                            ₹{balanceDue.toFixed(2)}
                          </span>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* 🟢 NEW: PAYMENT HISTORY TAB */}
          {activeTab === "payments" && (
            <div className="tab-pane fade show active">
              <div className="d-flex justify-content-between align-items-center mb-3">
                <h6 className="fw-bold text-secondary m-0">
                  <i className="bi bi-clock-history me-2"></i>Transaction Ledger
                </h6>
                <div className="d-flex gap-3">
                  <div className="bg-light px-3 py-1 rounded border">
                    <span className="text-muted small me-2">Total Bill:</span>
                    <span className="fw-bold">₹{order.summary?.total}</span>
                  </div>
                  <div className="bg-danger bg-opacity-10 px-3 py-1 rounded border border-danger border-opacity-25">
                    <span className="text-danger small me-2">Balance Due:</span>
                    <span className="fw-bold text-danger">₹{balanceDue.toFixed(2)}</span>
                  </div>
                </div>
              </div>

              <div className="table-responsive">
                <table className="table table-hover table-bordered text-center align-middle mt-2">
                  <thead className="table-light text-muted">
                    <tr>
                      <th>#</th>
                      <th>Date & Time</th>
                      <th>Transaction Type</th>
                      <th>Method</th>
                      <th className="text-end">Amount</th>
                    </tr>
                  </thead>
                  <tbody>
                    {order.payment_history.map((entry, idx) => (
                      <tr key={idx}>
                        <td>{idx + 1}</td>
                        <td className="text-muted fw-semibold">{entry.date}</td>
                        <td>
                          {entry.type === "credit_given" ? (
                            <span className="badge bg-warning text-dark px-3 py-2">
                              <i className="bi bi-journal-text me-1"></i> Credit Given
                            </span>
                          ) : entry.type === "refund" ? (
                            <span className="badge bg-danger px-3 py-2">
                              <i className="bi bi-arrow-return-left me-1"></i> Refund
                            </span>
                          ) : (
                            <span className="badge bg-success px-3 py-2">
                              <i className="bi bi-check-circle me-1"></i> Payment Received
                            </span>
                          )}
                        </td>
                        <td>
                          {entry.method === "online" ? (
                            <span className="text-primary fw-bold">
                              <i className="bi bi-phone me-1"></i>UPI
                            </span>
                          ) : entry.method === "cash" ? (
                            <span className="text-success fw-bold">
                              <i className="bi bi-cash me-1"></i>Cash
                            </span>
                          ) : (
                            <span className="text-muted">-</span>
                          )}
                        </td>
                        <td
                          className={`text-end fw-bold fs-6 ${
                            entry.type === "credit_given"
                              ? "text-danger"
                              : "text-success"
                          }`}
                        >
                          {entry.type === "credit_given" ? "-" : "+"}₹
                          {entry.amount.toFixed(2)}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </div>
      </Modal.Body>
      <Modal.Footer className="bg-light d-flex justify-content-between">
        <Button variant="secondary" onClick={handleHide}>
          Close Window
        </Button>
        <div className="d-flex gap-2 flex-nowrap align-items-center">
          <div className="btn-group shadow-sm">
            <button 
              type="button" 
              className="btn btn-outline-primary px-3 fw-bold" 
              onClick={() => handleDocumentAction(defaultDocMethod)}
              disabled={downloadingPdf}
              title={defaultDocMethod === 'pdf' ? "Download PDF" : defaultDocMethod === 'copy_img' ? "Copy Bill Image" : "Print Bill"}
            >
              {defaultDocMethod === 'pdf' ? (
                downloadingPdf ? <span className="spinner-border spinner-border-sm me-1"></span> : <i className="bi bi-file-earmark-pdf me-1"></i>
              ) : defaultDocMethod === 'copy_img' ? (
                downloadingPdf ? <span className="spinner-border spinner-border-sm me-1"></span> : <i className="bi bi-clipboard-data me-1"></i>
              ) : (
                <i className="bi bi-printer me-1"></i>
              )}
              {defaultDocMethod === 'pdf' ? "PDF" : defaultDocMethod === 'copy_img' ? "Copy Image" : "Print"}
            </button>
            <button 
              type="button" 
              className="btn btn-outline-primary dropdown-toggle dropdown-toggle-split" 
              data-bs-toggle="dropdown" 
              aria-expanded="false"
              disabled={downloadingPdf}
            >
              <span className="visually-hidden">Toggle Dropdown</span>
            </button>
            <ul className="dropdown-menu shadow border-0 mt-1">
              <li>
                <button className="dropdown-item py-2 d-flex justify-content-between align-items-center fw-medium" onClick={() => handleDocumentAction('print')}>
                  <span><i className="bi bi-printer me-2 text-primary fs-5"></i> Print Bill</span>
                  {defaultDocMethod === 'print' && <i className="bi bi-check-circle-fill text-success ms-3" title="Current Default"></i>}
                </button>
              </li>
              <li><hr className="dropdown-divider" /></li>
              <li>
                <button className="dropdown-item py-2 d-flex justify-content-between align-items-center fw-medium" onClick={() => handleDocumentAction('pdf')}>
                  <span><i className="bi bi-file-earmark-pdf me-2 text-danger fs-5"></i> Download PDF</span>
                  {defaultDocMethod === 'pdf' && <i className="bi bi-check-circle-fill text-success ms-3" title="Current Default"></i>}
                </button>
              </li>
              <li><hr className="dropdown-divider" /></li>
              <li>
                <button className="dropdown-item py-2 d-flex justify-content-between align-items-center fw-medium" onClick={() => handleDocumentAction('copy_img')}>
                  <span><i className="bi bi-clipboard-data me-2 text-secondary fs-5"></i> Copy Bill Image</span>
                  {defaultDocMethod === 'copy_img' && <i className="bi bi-check-circle-fill text-success ms-3" title="Current Default"></i>}
                </button>
              </li>
            </ul>
          </div>
          <Button 
            variant="outline-secondary" 
            className="fw-bold px-2" 
            onClick={handleCopyMessage} 
            title="Copy invoice message to clipboard"
          >
            <i className="bi bi-clipboard me-1"></i>Copy
          </Button>
          <div className="btn-group shadow-sm">
            <button 
              type="button" 
              className="btn btn-success fw-bold px-3" 
              onClick={() => handleShareWhatsApp(defaultWaMethod)} 
              title={`Send via your Default Method to ${order.customer?.name || 'Customer'}`}
            >
              <i className="bi bi-whatsapp me-2"></i>Send ({order.customer?.contact || "WhatsApp"})
            </button>
            <button 
              type="button" 
              className="btn btn-success dropdown-toggle dropdown-toggle-split" 
              data-bs-toggle="dropdown" 
              aria-expanded="false"
            >
              <span className="visually-hidden">Toggle Dropdown</span>
            </button>
            <ul className="dropdown-menu dropdown-menu-end shadow border-0 mt-1">
              <li>
                <button className="dropdown-item py-2 d-flex align-items-center fw-medium" onClick={() => handleShareWhatsApp('share')}>
                  <i className="bi bi-file-earmark-pdf me-2 text-danger fs-5"></i> Share with PDF Attached
                  {defaultWaMethod === 'share' && <i className="bi bi-check-circle-fill text-success ms-auto ms-2" title="Current Default"></i>}
                </button>
              </li>
              <li><hr className="dropdown-divider" /></li>
              <li>
                <button className="dropdown-item py-2 d-flex align-items-center fw-medium" onClick={() => handleShareWhatsApp('web')}>
                  <i className="bi bi-browser-chrome me-2 text-muted"></i> Send via WhatsApp Web
                  {defaultWaMethod === 'web' && <i className="bi bi-check-circle-fill text-success ms-auto ms-2" title="Current Default"></i>}
                </button>
              </li>
              <li><hr className="dropdown-divider" /></li>
              <li>
                <button className="dropdown-item py-2 d-flex align-items-center fw-medium" onClick={() => handleShareWhatsApp('app')}>
                  <i className="bi bi-display me-2 text-muted fs-5"></i> Open Desktop App directly
                  <div className="ms-auto d-flex align-items-center gap-2">
                    <i className="bi bi-lightning-fill text-warning" title="Fastest Method"></i>
                    {defaultWaMethod === 'app' && <i className="bi bi-check-circle-fill text-success" title="Current Default"></i>}
                  </div>
                </button>
              </li>
              <li><hr className="dropdown-divider" /></li>
              <li>
                <button className="dropdown-item py-2 d-flex align-items-center fw-medium" onClick={() => handleShareWhatsApp('api')}>
                  <i className="bi bi-link-45deg me-2 text-muted fs-5"></i> Default Link (wa.me)
                  {defaultWaMethod === 'api' && <i className="bi bi-check-circle-fill text-success ms-auto ms-2" title="Current Default"></i>}
                </button>
              </li>
            </ul>
          </div>
        </div>
      </Modal.Footer>
    </Modal>
  );
}

export default OrderDetailsModal;